package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.data.firebase.AttachmentStagingUploader
import com.elan.edubridge.data.firebase.AttachmentUploadResult
import com.elan.edubridge.data.firebase.AttachmentUploadStage
import com.elan.edubridge.shared.domain.model.AttachmentContentType
import com.elan.edubridge.shared.domain.model.CreateCommentRequest
import com.elan.edubridge.shared.domain.model.CreatePostRequest
import com.elan.edubridge.shared.domain.model.ContentReport
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.ContentReportTargetType
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.PostAttachment
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.UpdatePostRequest
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.formatTimestamp
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.files.File

/** 학원당 하나인 게시판의 스태프 콘솔. 글 작성·수정·삭제는 EDITOR 이상이고, 댓글은 활성 멤버십이면 쓴다. */
object BoardPage {
    /** 게시글 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "board-retry"

    private val scope = MainScope()
    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun open(session: SessionState, academyId: String) {
        scope.launch {
            val result = repository.postsResult(academyId)
            renderList(session, academyId, result.items, result.failure)
        }
    }

    private fun renderList(
        session: SessionState,
        academyId: String,
        posts: List<PostData>,
        failure: CommandFailure?,
    ) {
        val canWrite = canWritePosts(session)
        DashboardShell.render(session, academyId, "board", listHtml(posts, canWrite, failure)) {
            wireRetry(RETRY_ID) { open(session, academyId) }
            (document.getElementById("board-create") as? HTMLButtonElement)?.onclick = {
                openPostEditor(academyId, existing = null, allowAttachments = true) { post ->
                    openDetail(session, academyId, post.id)
                }
                null
            }
            wirePostLinks(session, academyId)
        }
    }

    private fun listHtml(posts: List<PostData>, canWrite: Boolean, failure: CommandFailure?): String {
        // 게시판이 조용한 것과 못 읽는 것은 다르다(#30) — 후자에 "첫 글을 남겨 보세요"를 띄우면
        // 이미 있는 글 위에 새 글을 쓰게 된다.
        val body = when {
            posts.isNotEmpty() -> posts.joinToString("") { postListItemHtml(it) }
            failure != null -> readErrorHtml(failure, RETRY_ID)
            else -> emptyListHtml(canWrite)
        }
        return """
            <div class="eb-page-header">
                <div>
                    <h1 class="eb-page-title">게시판</h1>
                    <p class="eb-meta">학원 구성원과 운영 소식을 나눠 보세요.</p>
                </div>
                ${if (canWrite) """<button class="eb-button eb-button--primary" id="board-create" type="button">${icon("icon-plus", "sm")}글 작성</button>""" else ""}
            </div>
            <section class="eb-card">$body</section>
        """.trimIndent()
    }

    private fun emptyListHtml(canWrite: Boolean): String = """
        <div class="eb-empty-state">
            ${icon("icon-notice", "xl")}
            <h2>등록된 게시글이 없어요</h2>
            <p>${if (canWrite) "학원 운영 소식이나 안내를 첫 글로 남겨 보세요." else "선생님이 게시글을 올리면 여기에 나타나요."}</p>
        </div>
    """.trimIndent()

    private fun postListItemHtml(post: PostData): String = """
        <article class="eb-list-row">
            ${icon("icon-notice", "md")}
            <div>
                <button class="eb-button eb-button--link board-open" type="button" data-post-id="${post.id.escapeHtml()}">${post.title.escapeHtml()}</button>
                <p class="eb-meta">${authorAndTime(post.authorName, post.createdAt)} · 댓글 ${post.commentCount}개${attachmentSuffix(post)}</p>
            </div>
            ${icon("icon-chevron-right", "xs")}
        </article>
    """.trimIndent()

    private fun wirePostLinks(session: SessionState, academyId: String) {
        val buttons = document.querySelectorAll(".board-open")
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                button.getAttribute("data-post-id")?.let { openDetail(session, academyId, it) }
                null
            }
        }
    }

    private fun openDetail(session: SessionState, academyId: String, postId: String) {
        scope.launch {
            val result = repository.postResult(academyId, postId)
            val post = result.item
            if (post == null) {
                // 삭제된 글과 못 읽은 글에 같은 토스트를 띄우면, 네트워크가 끊겼을 때
                // 사용자가 글이 지워진 줄 안다(#30).
                Toast.error(
                    if (result.isEmptySuccess) {
                        "게시글을 찾을 수 없어요."
                    } else {
                        "게시글을 불러오지 못했어요. 잠시 후 다시 시도해 주세요."
                    },
                )
                open(session, academyId)
                return@launch
            }
            renderDetail(session, academyId, post, repository.postCommentsResult(academyId, postId).items)
        }
    }

    private fun renderDetail(session: SessionState, academyId: String, post: PostData, comments: List<PostComment>) {
        val canWrite = canWritePosts(session)
        DashboardShell.render(session, academyId, "board", detailHtml(session, post, comments, canWrite)) {
            (document.getElementById("board-back") as? HTMLButtonElement)?.onclick = { open(session, academyId); null }
            (document.getElementById("board-edit") as? HTMLButtonElement)?.onclick = {
                openPostEditor(academyId, post, allowAttachments = true) { savedPost ->
                    openDetail(session, academyId, savedPost.id)
                }
                null
            }
            (document.getElementById("board-delete") as? HTMLButtonElement)?.onclick = {
                deletePost(session, academyId, post)
                null
            }
            (document.getElementById("board-attach") as? HTMLButtonElement)?.onclick = {
                openPostAttachmentEditor(academyId, post) { savedPost ->
                    openDetail(session, academyId, savedPost.id)
                }
                null
            }
            wireAttachmentButtons(academyId, post)
            wireComments(session, academyId, post, comments)
            wireReportActions(session, academyId, post, comments)
        }
    }

    private fun detailHtml(
        session: SessionState,
        post: PostData,
        comments: List<PostComment>,
        canWrite: Boolean,
    ): String = """
        <div class="eb-page-header">
            <button class="eb-button eb-button--ghost" id="board-back" type="button">목록으로</button>
            ${if (canWrite) """
                <div>
                    <button class="eb-button eb-button--ghost" id="board-edit" type="button">수정</button>
                    ${if (post.attachments.size < MAX_ATTACHMENT_COUNT) """<button class="eb-button eb-button--ghost" id="board-attach" type="button">파일 첨부</button>""" else ""}
                    <button class="eb-button eb-button--ghost" id="board-delete" type="button">삭제</button>
                </div>
            """ else ""}
            ${if (!session.requiresGuardianConsent) """
                <div>
                    <button class="eb-button eb-button--ghost" id="board-report-post" type="button">게시글 신고</button>
                    ${if (post.authorUid != null && post.authorUid != session.account?.id) """<button class="eb-button eb-button--ghost" id="board-report-author" type="button">작성자 신고</button>""" else ""}
                </div>
            """ else ""}
        </div>
        <article class="eb-card" aria-labelledby="board-post-title">
            <h1 class="eb-page-title" id="board-post-title">${post.title.escapeHtml()}</h1>
            <p class="eb-meta">${authorAndTime(post.authorName, post.createdAt)}${post.updatedAt?.let { " · 수정 ${formatTimestamp(it)}" }.orEmpty()}</p>
            <div style="white-space: pre-wrap;">${post.content.escapeHtml()}</div>
            ${attachmentsHtml(post)}
        </article>
        <section class="eb-card" aria-labelledby="board-comments-title">
            <h2 id="board-comments-title">댓글 ${comments.size}개</h2>
            <form id="board-comment-form">
                <div class="eb-field">
                    <label for="board-comment-content">댓글</label>
                    <textarea id="board-comment-content" maxlength="1000" rows="3" required placeholder="게시글에 대한 의견을 남겨 보세요."></textarea>
                </div>
                <p class="eb-form-error" id="board-comment-error" role="alert" hidden></p>
                <button class="eb-button eb-button--primary" type="submit" id="board-comment-submit">댓글 등록</button>
            </form>
            ${if (comments.isEmpty()) """<p class="eb-meta">첫 댓글을 남겨 보세요.</p>""" else comments.joinToString("") { comment -> commentHtml(session, comment, canWrite, !session.requiresGuardianConsent) }}
        </section>
    """.trimIndent()

    private fun attachmentsHtml(post: PostData): String {
        if (post.attachments.isEmpty()) return ""
        return """
            <section aria-label="첨부 파일">
                <h2>첨부 파일</h2>
                ${post.attachments.joinToString("") { attachment ->
                    """<button class="eb-button eb-button--ghost eb-button--sm board-attachment" type="button" data-attachment-id="${attachment.id.escapeHtml()}">${attachment.fileName.escapeHtml()} · ${formatBytes(attachment.sizeBytes)}</button>"""
                }}
            </section>
        """.trimIndent()
    }

    private fun commentHtml(session: SessionState, comment: PostComment, canWrite: Boolean, canReport: Boolean): String {
        val canDelete = canWrite || comment.authorUid == session.account?.id
        return """
            <article class="eb-list-row">
                ${icon("icon-person", "md")}
                <div>
                    <strong>${(comment.authorName ?: "구성원").escapeHtml()}</strong>
                    <span class="eb-meta">${formatTimestamp(comment.createdAt)}</span>
                    <p style="white-space: pre-wrap;">${comment.content.escapeHtml()}</p>
                </div>
                ${if (canReport) """<button class="eb-button eb-button--ghost eb-button--sm board-report-comment" type="button" data-comment-id="${comment.id.escapeHtml()}">신고</button>""" else ""}
                ${if (canReport && comment.authorUid != null && comment.authorUid != session.account?.id) """<button class="eb-button eb-button--ghost eb-button--sm board-report-comment-author" type="button" data-comment-id="${comment.id.escapeHtml()}">작성자 신고</button>""" else ""}
                ${if (canDelete) """<button class="eb-button eb-button--ghost eb-button--sm board-comment-delete" type="button" data-comment-id="${comment.id.escapeHtml()}">삭제</button>""" else ""}
            </article>
        """.trimIndent()
    }

    private fun wireComments(session: SessionState, academyId: String, post: PostData, comments: List<PostComment>) {
        val form = document.getElementById("board-comment-form") as? HTMLFormElement
        form?.onsubmit = { event ->
            event.preventDefault()
            submitComment(session, academyId, post.id)
            null
        }
        val deleteButtons = document.querySelectorAll(".board-comment-delete")
        for (index in 0 until deleteButtons.length) {
            val button = deleteButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val commentId = button.getAttribute("data-comment-id")
                if (commentId != null) deleteComment(session, academyId, post.id, commentId)
                null
            }
        }
    }

    private fun wireReportActions(session: SessionState, academyId: String, post: PostData, comments: List<PostComment>) {
        if (session.requiresGuardianConsent) return
        (document.getElementById("board-report-post") as? HTMLButtonElement)?.onclick = {
            ContentReportModal.open(academyId, ContentReportTargetType.POST, post.id)
            null
        }
        (document.getElementById("board-report-author") as? HTMLButtonElement)?.onclick = {
            post.authorUid?.let { ContentReportModal.open(academyId, ContentReportTargetType.USER, it) }
            null
        }
        val commentButtons = document.querySelectorAll(".board-report-comment")
        for (index in 0 until commentButtons.length) {
            val button = commentButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                comments.firstOrNull { it.id == button.getAttribute("data-comment-id") }?.let { comment ->
                    ContentReportModal.open(academyId, ContentReportTargetType.COMMENT, comment.id, post.id)
                }
                null
            }
        }
        val authorButtons = document.querySelectorAll(".board-report-comment-author")
        for (index in 0 until authorButtons.length) {
            val button = authorButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                comments.firstOrNull { it.id == button.getAttribute("data-comment-id") }?.authorUid?.let { authorUid ->
                    ContentReportModal.open(academyId, ContentReportTargetType.USER, authorUid)
                }
                null
            }
        }
    }

    /** 신고 대기열에서 원문을 확인할 때만 쓴다. 화면에는 식별자를 렌더하지 않는다. */
    fun openReportedTarget(session: SessionState, academyId: String, report: ContentReport) {
        val postId = when (report.targetType) {
            ContentReportTargetType.POST -> report.targetId
            ContentReportTargetType.COMMENT -> report.postId
            ContentReportTargetType.USER -> null
        }
        if (postId.isNullOrBlank()) {
            Toast.error("이 신고 대상은 게시판에서 열 수 없어요.")
        } else {
            openDetail(session, academyId, postId)
        }
    }

    private fun submitComment(session: SessionState, academyId: String, postId: String) {
        val content = (document.getElementById("board-comment-content") as? HTMLTextAreaElement)?.value?.trim().orEmpty()
        val error = document.getElementById("board-comment-error") as? HTMLElement
        if (content.isEmpty()) {
            showError(error, "댓글을 입력해 주세요.")
            return
        }
        if (content.length > 1000) {
            showError(error, "댓글은 1,000자 이하로 입력해 주세요.")
            return
        }
        (document.getElementById("board-comment-submit") as? HTMLButtonElement)?.disabled = true
        scope.launch {
            val result = commandRepository.createComment(CreateCommentRequest(academyId, postId, content))
            if (result.isSuccess) {
                Toast.success("댓글을 등록했어요.")
                openDetail(session, academyId, postId)
            } else {
                showError(error, result.errorMessage ?: "댓글을 등록하지 못했어요. 다시 시도해 주세요.")
                (document.getElementById("board-comment-submit") as? HTMLButtonElement)?.disabled = false
            }
        }
    }

    private fun deleteComment(session: SessionState, academyId: String, postId: String, commentId: String) {
        if (!window.confirm("이 댓글을 삭제할까요?")) return
        scope.launch {
            val result = commandRepository.deleteComment(academyId, postId, commentId)
            if (result.isSuccess) {
                Toast.success("댓글을 삭제했어요.")
                openDetail(session, academyId, postId)
            } else {
                Toast.error(result.errorMessage ?: "댓글을 삭제하지 못했어요. 다시 시도해 주세요.")
            }
        }
    }

    private fun deletePost(session: SessionState, academyId: String, post: PostData) {
        if (!window.confirm("게시글을 삭제할까요? 첨부 파일도 더 이상 열 수 없어요.")) return
        scope.launch {
            val result = commandRepository.deletePost(academyId, post.id)
            if (result.isSuccess) {
                Toast.success("게시글을 삭제했어요.")
                open(session, academyId)
            } else {
                Toast.error(result.errorMessage ?: "게시글을 삭제하지 못했어요. 다시 시도해 주세요.")
            }
        }
    }

    /** 게시판 두 화면이 공유하는 제목·본문 편집기. 새 글은 먼저 만든 뒤 첨부를 별도로 승격한다. */
    fun openPostEditor(
        academyId: String,
        existing: PostData?,
        allowAttachments: Boolean,
        onSaved: (PostData) -> Unit,
    ) {
        val isNew = existing == null
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="board-editor-title">
                <div class="eb-modal__header">
                    <h2 id="board-editor-title">${if (isNew) "게시글 작성" else "게시글 수정"}</h2>
                    <button class="eb-icon-button" id="board-editor-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
                </div>
                <form id="board-editor-form">
                    <div class="eb-field"><label for="board-editor-title-input">제목</label><input id="board-editor-title-input" maxlength="100" required value="${existing?.title?.escapeHtml() ?: ""}"></div>
                    <div class="eb-field"><label for="board-editor-content">내용</label><textarea id="board-editor-content" maxlength="5000" rows="10" required>${existing?.content?.escapeHtml() ?: ""}</textarea></div>
                    <p class="eb-meta">다른 학생의 이름·사진을 올리지 말아 주세요.</p>
                    ${if (isNew && allowAttachments) """
                        <div class="eb-field">
                            <label for="board-editor-files">첨부 파일</label>
                            <input id="board-editor-files" type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf">
                            <p class="eb-meta">JPEG, PNG, WEBP 또는 PDF · 파일당 10MB 이하 · 최대 5개</p>
                            <div id="board-editor-file-list" aria-live="polite"></div>
                        </div>
                    """ else ""}
                    <p class="eb-form-error" id="board-editor-error" role="alert" hidden></p>
                </form>
                <div class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="board-editor-cancel" type="button">취소</button>
                    <button class="eb-button eb-button--primary" id="board-editor-submit" type="submit" form="board-editor-form">저장</button>
                </div>
            </div>
        """.trimIndent()
        document.body?.appendChild(backdrop)

        var saving = false
        fun close() { if (!saving) backdrop.remove() }
        (backdrop.querySelector("#board-editor-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#board-editor-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }

        val fileInput = backdrop.querySelector("#board-editor-files") as? HTMLInputElement
        fileInput?.onchange = { renderSelectedFiles(backdrop, selectedFiles(fileInput)); null }
        (backdrop.querySelector("#board-editor-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            if (!saving) {
                saving = true
                submitPostEditor(
                    academyId,
                    existing,
                    allowAttachments,
                    backdrop,
                    selectedFiles(fileInput),
                    { saving = false },
                    ::close,
                    onSaved,
                )
            }
            null
        }
    }

    fun hasAttachmentCapacity(post: PostData): Boolean = post.attachments.size < MAX_ATTACHMENT_COUNT

    private fun submitPostEditor(
        academyId: String,
        existing: PostData?,
        allowAttachments: Boolean,
        backdrop: HTMLElement,
        files: List<File>,
        allowRetry: () -> Unit,
        close: () -> Unit,
        onSaved: (PostData) -> Unit,
    ) {
        val title = (backdrop.querySelector("#board-editor-title-input") as? HTMLInputElement)?.value?.trim().orEmpty()
        val content = (backdrop.querySelector("#board-editor-content") as? HTMLTextAreaElement)?.value?.trim().orEmpty()
        val error = backdrop.querySelector("#board-editor-error") as? HTMLElement
        when {
            title.isEmpty() -> { showError(error, "제목을 입력해 주세요."); allowRetry(); return }
            title.length > 100 -> { showError(error, "제목은 100자 이하로 입력해 주세요."); allowRetry(); return }
            content.isEmpty() -> { showError(error, "내용을 입력해 주세요."); allowRetry(); return }
            content.length > 5000 -> { showError(error, "내용은 5,000자 이하로 입력해 주세요."); allowRetry(); return }
            files.size > 5 -> { showError(error, "첨부 파일은 최대 5개까지 선택할 수 있어요."); allowRetry(); return }
            files.any { attachmentType(it) == null } -> { showError(error, "JPEG, PNG, WEBP 또는 PDF만 첨부할 수 있어요."); allowRetry(); return }
            files.any { it.size.toDouble() >= MAX_ATTACHMENT_BYTES.toDouble() } -> { showError(error, "첨부 파일은 각각 10MiB보다 작아야 해요."); allowRetry(); return }
        }
        scope.launch {
            if (existing == null) {
                val result = commandRepository.createPost(CreatePostRequest(academyId, title, content))
                val post = result.post
                if (!result.isSuccess || post == null) {
                    showError(error, result.errorMessage ?: "게시글을 등록하지 못했어요. 다시 시도해 주세요.")
                    allowRetry()
                    return@launch
                }
                allowRetry()
                if (files.isEmpty() || !allowAttachments) {
                    close()
                    Toast.success("게시글을 등록했어요.")
                    onSaved(post)
                } else {
                    close()
                    openPostAttachmentEditor(academyId, post, files, onSaved)
                }
            } else {
                val result = commandRepository.updatePost(UpdatePostRequest(academyId, existing.id, title, content))
                if (result.isSuccess) {
                    allowRetry()
                    close()
                    Toast.success("게시글을 수정했어요.")
                    onSaved(existing)
                } else {
                    showError(error, result.errorMessage ?: "게시글을 수정하지 못했어요. 다시 시도해 주세요.")
                    allowRetry()
                }
            }
        }
    }

    /**
     * 이미 존재하는 글에 Storage SDK 스테이징 업로드를 붙인다.
     *
     * 실패한 파일은 같은 uploadId와 File 객체를 보존해 재시도한다. 따라서 첨부 실패 뒤 저장을
     * 다시 눌러도 새 게시글을 만들지 않고, 서버 승격만 안전하게 반복한다.
     */
    fun openPostAttachmentEditor(
        academyId: String,
        post: PostData,
        initialFiles: List<File> = emptyList(),
        onSaved: (PostData) -> Unit,
    ) {
        val capacity = MAX_ATTACHMENT_COUNT - post.attachments.size
        if (capacity <= 0) {
            Toast.error("이 게시글에는 첨부 파일을 최대 5개까지 등록할 수 있어요.")
            return
        }

        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="board-attachment-title">
                <div class="eb-modal__header">
                    <h2 id="board-attachment-title">파일 첨부</h2>
                    <button class="eb-icon-button" id="board-attachment-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
                </div>
                <form id="board-attachment-form">
                    <div class="eb-field">
                        <label for="board-attachment-files">첨부 파일</label>
                        <input id="board-attachment-files" type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf">
                        <p class="eb-meta">JPEG, PNG, WEBP 또는 PDF · 파일당 10MiB 미만 · 최대 ${capacity}개</p>
                        <p class="eb-meta">다른 학생의 이름·사진을 올리지 말아 주세요.</p>
                        <div id="board-attachment-file-list" aria-live="polite"></div>
                    </div>
                    <p class="eb-form-error" id="board-attachment-error" role="alert" hidden></p>
                </form>
                <div class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="board-attachment-cancel" type="button">취소</button>
                    <button class="eb-button eb-button--primary" id="board-attachment-submit" type="submit" form="board-attachment-form">파일 등록</button>
                </div>
            </div>
        """.trimIndent()
        document.body?.appendChild(backdrop)

        var pending = initialFiles.map { PendingAttachment(it, AttachmentStagingUploader.newUploadId()) }
        var saving = false
        var completed = false
        val fileInput = backdrop.querySelector("#board-attachment-files") as? HTMLInputElement
        val submit = backdrop.querySelector("#board-attachment-submit") as? HTMLButtonElement
        val error = backdrop.querySelector("#board-attachment-error") as? HTMLElement

        fun close() {
            if (!saving) backdrop.remove()
        }

        fun refresh() {
            renderPendingAttachments(backdrop, pending)
            fileInput?.disabled = pending.any { it.result != null }
            submit?.disabled = saving || pending.isEmpty() || completed
        }

        fun finishWhenComplete() {
            if (completed || pending.isEmpty() || pending.any { it.result?.isSuccess != true }) return
            completed = true
            backdrop.remove()
            Toast.success("첨부 파일을 등록했어요.")
            onSaved(post)
        }

        lateinit var upload: (List<PendingAttachment>) -> Unit

        fun wireRetries() {
            val retryButtons = backdrop.querySelectorAll(".board-attachment-retry")
            for (index in 0 until retryButtons.length) {
                val button = retryButtons.item(index) as? HTMLElement ?: continue
                button.onclick = {
                    val attachmentIndex = button.getAttribute("data-attachment-index")?.toIntOrNull()
                    val attachment = attachmentIndex?.let { pending.getOrNull(it) }
                    if (!saving && attachment != null) upload(listOf(attachment))
                    null
                }
            }
        }

        fun refreshAndWire() {
            refresh()
            wireRetries()
        }

        upload = { items ->
            val invalid = validateAttachments(pending, capacity)
            if (invalid != null) {
                showError(error, invalid)
                refreshAndWire()
            } else {
                saving = true
                refreshAndWire()
                scope.launch {
                    items.forEach { attachment ->
                        attachment.result = AttachmentStagingUploader.uploadAndAttach(
                            academyId = academyId,
                            postId = post.id,
                            file = attachment.file,
                            uploadId = attachment.uploadId,
                        )
                    }
                    saving = false
                    refreshAndWire()
                    val failures = pending.filter { it.result?.isSuccess != true }
                    if (failures.isEmpty()) {
                        error?.setAttribute("hidden", "")
                        finishWhenComplete()
                    } else {
                        showError(error, "첨부를 등록하지 못한 파일이 있어요. 파일별 안내를 확인한 뒤 다시 시도해 주세요.")
                    }
                }
            }
        }

        (backdrop.querySelector("#board-attachment-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#board-attachment-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }
        fileInput?.onchange = {
            if (!saving && pending.none { it.result != null }) {
                pending = selectedFiles(fileInput).map { PendingAttachment(it, AttachmentStagingUploader.newUploadId()) }
                refreshAndWire()
            }
            null
        }
        (backdrop.querySelector("#board-attachment-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            if (!saving && !completed) upload(pending.filter { it.result?.isSuccess != true })
            null
        }
        refreshAndWire()
        if (pending.isNotEmpty()) upload(pending)
    }

    private data class PendingAttachment(
        val file: File,
        val uploadId: String,
        var result: AttachmentUploadResult? = null,
    )

    private fun renderPendingAttachments(backdrop: HTMLElement, pending: List<PendingAttachment>) {
        val target = backdrop.querySelector("#board-attachment-file-list") as? HTMLElement ?: return
        target.innerHTML = pending.mapIndexed { index, attachment ->
            val result = attachment.result
            val state = when {
                result == null -> "등록 대기"
                result.isSuccess -> "등록됨"
                else -> attachmentFailureDetail(result)
            }
            val stateClass = when {
                result == null -> "eb-chip--info"
                result.isSuccess -> "eb-chip--success"
                else -> "eb-chip--error"
            }
            val retry = if (result?.stage == AttachmentUploadStage.STAGING || result?.stage == AttachmentUploadStage.ATTACH) {
                """<button class="eb-button eb-button--ghost eb-button--sm board-attachment-retry" type="button" data-attachment-index="$index">재시도</button>"""
            } else {
                ""
            }
            """
                <div class="eb-list-row">
                    <div><strong>${attachment.file.name.escapeHtml()}</strong><p class="eb-meta">${formatBytes(attachment.file.size.toInt())}</p></div>
                    <div><span class="eb-chip $stateClass">${state.escapeHtml()}</span>$retry</div>
                </div>
            """.trimIndent()
        }.joinToString("")
    }

    private fun attachmentFailureDetail(result: AttachmentUploadResult): String {
        // renderPendingAttachments escapes this text before adding it to innerHTML.
        result.errorMessage?.takeIf { it.isNotBlank() }?.let { return it }
        return when (result.errorCode) {
            "permission-denied" -> "첨부 파일을 등록할 권한이 없어요."
            "unavailable" -> "파일 서버에 연결할 수 없어요. 네트워크를 확인한 뒤 다시 시도해 주세요."
            else -> when (result.stage) {
                AttachmentUploadStage.STAGING -> "임시 저장 실패 · 네트워크 확인 후 재시도"
                AttachmentUploadStage.ATTACH -> when (result.errorCode) {
                    "not-found" -> "임시 파일 없음 · 파일을 다시 올린 뒤 재시도"
                    "failed-precondition" -> "첨부 한도 초과 또는 삭제된 글"
                    "invalid-argument" -> "파일 형식 또는 크기 확인 필요"
                    else -> "첨부 파일 등록 실패 · 다시 시도"
                }
                AttachmentUploadStage.INPUT -> "파일 형식 또는 크기 확인 필요"
                AttachmentUploadStage.SUCCESS -> "등록됨"
            }
        }
    }

    private fun validateAttachments(pending: List<PendingAttachment>, capacity: Int): String? = when {
        pending.isEmpty() -> "첨부할 파일을 선택해 주세요."
        pending.size > capacity -> "첨부 파일은 이 게시글에 최대 ${capacity}개까지 등록할 수 있어요."
        pending.any { attachmentType(it.file) == null } -> "JPEG, PNG, WEBP 또는 PDF만 첨부할 수 있어요."
        pending.any { it.file.size.toDouble() >= MAX_ATTACHMENT_BYTES.toDouble() } -> "첨부 파일은 각각 10MiB보다 작아야 해요."
        else -> null
    }

    private fun wireAttachmentButtons(academyId: String, post: PostData) {
        val buttons = document.querySelectorAll(".board-attachment")
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val attachmentId = button.getAttribute("data-attachment-id")
                post.attachments.firstOrNull { it.id == attachmentId }?.let { openAttachment(academyId, post.id, it) }
                null
            }
        }
    }

    private fun openAttachment(academyId: String, postId: String, attachment: PostAttachment) {
        scope.launch {
            val ticket = commandRepository.getPostAttachmentUrl(academyId, postId, attachment.id)
            val url = ticket.downloadUrl
            if (!ticket.isSuccess || url == null) {
                Toast.error(ticket.errorMessage ?: "첨부 파일 주소를 열지 못했어요. 다시 시도해 주세요.")
                return@launch
            }
            val anchor = document.createElement("a") as HTMLAnchorElement
            anchor.href = url
            anchor.target = "_blank"
            anchor.rel = "noopener"
            document.body?.appendChild(anchor)
            anchor.click()
            anchor.remove()
        }
    }

    private fun renderSelectedFiles(backdrop: HTMLElement, files: List<File>) {
        val target = backdrop.querySelector("#board-editor-file-list") as? HTMLElement ?: return
        target.innerHTML = when {
            files.isEmpty() -> ""
            files.size > 5 -> """<p class="eb-form-error">첨부 파일은 최대 5개까지 선택할 수 있어요.</p>"""
            else -> files.joinToString("") { file ->
                val unsupported = attachmentType(file) == null
                val oversized = file.size.toDouble() >= MAX_ATTACHMENT_BYTES.toDouble()
                val note = when {
                    unsupported -> "지원하지 않는 형식"
                    oversized -> "10MiB 이상"
                    else -> formatBytes(file.size.toInt())
                }
                """<span class="eb-chip ${if (unsupported || oversized) "eb-chip--error" else "eb-chip--info"}">${file.name.escapeHtml()} · $note</span>"""
            }
        }
    }

    private fun selectedFiles(input: HTMLInputElement?): List<File> {
        val files = input?.files ?: return emptyList()
        return (0 until files.length).mapNotNull { files.item(it) }
    }

    private fun attachmentType(file: File): AttachmentContentType? = AttachmentContentType.fromMimeType(file.type)

    private fun canWritePosts(session: SessionState): Boolean =
        !session.requiresGuardianConsent && session.activePermission.atLeast(Permission.EDITOR)

    private fun showError(target: HTMLElement?, message: String) {
        target?.textContent = message
        target?.removeAttribute("hidden")
    }

    private fun authorAndTime(author: String?, createdAt: String): String =
        "${(author ?: "운영자").escapeHtml()} · ${formatTimestamp(createdAt)}"

    private fun attachmentSuffix(post: PostData): String =
        if (post.attachments.isEmpty()) "" else " · 첨부 ${post.attachments.size}개"


    private fun formatBytes(sizeBytes: Int): String = when {
        sizeBytes < 1024 -> "$sizeBytes B"
        sizeBytes < 1024 * 1024 -> "${sizeBytes / 1024} KB"
        else -> {
            val tenthsOfMegabyte = sizeBytes * 10 / (1024 * 1024)
            "${tenthsOfMegabyte / 10}.${tenthsOfMegabyte % 10} MB"
        }
    }

    private const val MAX_ATTACHMENT_COUNT = 5
    private const val MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
}
